import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import AudioButton from "../components/AudioButton";
import {
  Trophy,
  Clock,
  Target,
  Star,
  RotateCcw,
  Home,
  Sparkles,
  Volume2,
  Zap,
  Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DIFFICULTY = {
  facile: { pairs: 6, time: 120, stars: [30, 20, 15] }, // 12 cartes
  moyen: { pairs: 8, time: 180, stars: [40, 30, 20] },  // 16 cartes
  difficile: { pairs: 10, time: 240, stars: [50, 40, 30] } // 20 cartes
};

export default function MemoryGame() {
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState(null);
  const [words, setWords] = useState([]);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [loading, setLoading] = useState(false);

  // Timer
  useEffect(() => {
    let interval;
    if (gameStarted && !gameWon) {
      interval = setInterval(() => {
        setTime(t => t + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameWon]);

  // Fetch words
  const fetchWords = async (level) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/words?limit=100&approved=true`);
      if (response.ok) {
        const data = await response.json();
        // Shuffle and take required number
        const shuffled = data.sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, DIFFICULTY[level].pairs);
        setWords(selected);
        initializeGame(selected);
      }
    } catch (error) {
      console.error("Error fetching words:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize game cards
  const initializeGame = (selectedWords) => {
    const gameCards = [];
    
    selectedWords.forEach((word, index) => {
      // Card with Baoulé word
      gameCards.push({
        id: `baoule-${index}`,
        word_id: word.word_id,
        text: word.baoulé,
        phonetic: word.phonétique,
        type: 'baoule',
        pairId: index
      });
      
      // Card with French translation
      gameCards.push({
        id: `french-${index}`,
        word_id: word.word_id,
        text: word.français,
        type: 'french',
        pairId: index
      });
    });
    
    // Shuffle cards
    const shuffled = gameCards.sort(() => 0.5 - Math.random());
    setCards(shuffled);
  };

  // Start game
  const startGame = (level) => {
    setDifficulty(level);
    setMoves(0);
    setTime(0);
    setFlippedCards([]);
    setMatchedPairs([]);
    setGameWon(false);
    setGameStarted(true);
    fetchWords(level);
  };

  // Handle card click
  const handleCardClick = (card) => {
    // Ignore if game not started, card already flipped, or already matched
    if (!gameStarted || flippedCards.includes(card.id) || matchedPairs.includes(card.pairId)) {
      return;
    }

    // Ignore if 2 cards already flipped
    if (flippedCards.length === 2) {
      return;
    }

    const newFlipped = [...flippedCards, card.id];
    setFlippedCards(newFlipped);

    // Check for match if 2 cards flipped
    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      
      const card1 = cards.find(c => c.id === newFlipped[0]);
      const card2 = cards.find(c => c.id === newFlipped[1]);

      if (card1.pairId === card2.pairId) {
        // Match found!
        setTimeout(() => {
          setMatchedPairs(prev => [...prev, card1.pairId]);
          setFlippedCards([]);
          
          // Check if game won
          if (matchedPairs.length + 1 === DIFFICULTY[difficulty].pairs) {
            setGameWon(true);
            setGameStarted(false);
          }
        }, 500);
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  // Calculate stars
  const getStars = () => {
    if (!gameWon) return 0;
    const stars = DIFFICULTY[difficulty].stars;
    if (moves <= stars[2]) return 3;
    if (moves <= stars[1]) return 2;
    if (moves <= stars[0]) return 1;
    return 1;
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Restart game
  const restartGame = () => {
    if (difficulty) {
      startGame(difficulty);
    } else {
      setDifficulty(null);
      setGameWon(false);
    }
  };

  // Difficulty selection screen
  if (!difficulty) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-background to-pink-50 dark:from-gray-900 dark:via-background dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-4"
            >
              <Home className="w-4 h-4" />
              Retour à l'accueil
            </button>
            
            <div className="inline-flex items-center gap-3 mb-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold">
                Memory Game
              </h1>
            </div>
            <p className="text-muted-foreground text-lg">
              Trouve les paires baoulé ↔ français !
            </p>
          </div>

          {/* Difficulty cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Facile */}
            <Card 
              className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all border-2 border-green-500/20 hover:border-green-500"
              onClick={() => startGame('facile')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Zap className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">Facile</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Parfait pour débuter !
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <Target className="w-4 h-4 text-green-600" />
                    <span>12 cartes (6 paires)</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4 text-green-600" />
                    <span>Temps libre</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>3★ en ≤15 coups</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Moyen */}
            <Card 
              className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all border-2 border-orange-500/20 hover:border-orange-500"
              onClick={() => startGame('moyen')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <Target className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">Moyen</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Un bon challenge !
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <Target className="w-4 h-4 text-orange-600" />
                    <span>16 cartes (8 paires)</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <span>Temps libre</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>3★ en ≤20 coups</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Difficile */}
            <Card 
              className="cursor-pointer hover:shadow-xl hover:scale-105 transition-all border-2 border-red-500/20 hover:border-red-500"
              onClick={() => startGame('difficile')}
            >
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trophy className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="font-heading text-2xl font-bold mb-2">Difficile</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Pour les champions !
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center gap-2">
                    <Target className="w-4 h-4 text-red-600" />
                    <span>20 cartes (10 paires)</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4 text-red-600" />
                    <span>Temps libre</span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span>3★ en ≤30 coups</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Instructions */}
          <Card className="mt-8 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <CardContent className="p-6">
              <h3 className="font-heading text-xl font-bold mb-3 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Comment jouer ?
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    1
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Retourne les cartes</p>
                    <p className="text-muted-foreground">Clique sur 2 cartes pour les révéler</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    2
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Trouve les paires</p>
                    <p className="text-muted-foreground">Associe le mot baoulé à sa traduction française</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    3
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Minimise tes coups</p>
                    <p className="text-muted-foreground">Moins de coups = plus d'étoiles !</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    4
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Gagne des étoiles</p>
                    <p className="text-muted-foreground">Collectionne 3 étoiles sur chaque niveau !</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Game screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-background to-pink-50 dark:from-gray-900 dark:via-background dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header with stats */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => setDifficulty(null)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Home className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
              <Clock className="w-4 h-4 text-primary" />
              <span className="font-mono font-semibold">{formatTime(time)}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
              <Target className="w-4 h-4 text-primary" />
              <span className="font-semibold">{moves} coups</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-card border border-border">
              <Trophy className="w-4 h-4 text-primary" />
              <span className="font-semibold">
                {matchedPairs.length}/{DIFFICULTY[difficulty].pairs}
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={restartGame}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Recommencer
          </Button>
        </div>

        {/* Game board */}
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Préparation du jeu...</p>
          </div>
        ) : (
          <div 
            className={`grid gap-4 ${
              difficulty === 'facile' 
                ? 'grid-cols-3 sm:grid-cols-4' 
                : difficulty === 'moyen'
                ? 'grid-cols-4 sm:grid-cols-4'
                : 'grid-cols-4 sm:grid-cols-5'
            }`}
          >
            {cards.map((card) => {
              const isFlipped = flippedCards.includes(card.id);
              const isMatched = matchedPairs.includes(card.pairId);
              
              return (
                <div
                  key={card.id}
                  onClick={() => handleCardClick(card)}
                  className={`
                    relative aspect-[3/4] cursor-pointer transition-all duration-300
                    ${isMatched ? 'opacity-50 pointer-events-none' : ''}
                  `}
                  style={{ perspective: '1000px' }}
                >
                  <div
                    className={`
                      relative w-full h-full transition-transform duration-500
                      ${isFlipped || isMatched ? '[transform:rotateY(180deg)]' : ''}
                    `}
                    style={{ transformStyle: 'preserve-3d' }}
                  >
                    {/* Card back */}
                    <Card 
                      className={`
                        absolute w-full h-full backface-hidden
                        bg-gradient-to-br from-primary to-purple-600
                        border-2 border-primary/50
                        ${!isFlipped && !isMatched ? 'hover:scale-105 hover:shadow-lg' : ''}
                      `}
                      style={{ backfaceVisibility: 'hidden' }}
                    >
                      <CardContent className="h-full flex items-center justify-center">
                        <Sparkles className="w-12 h-12 text-white" />
                      </CardContent>
                    </Card>

                    {/* Card front */}
                    <Card 
                      className={`
                        absolute w-full h-full backface-hidden
                        ${card.type === 'baoule' 
                          ? 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950 border-blue-500/30' 
                          : 'bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950 dark:to-purple-950 border-pink-500/30'
                        }
                        ${isMatched ? 'border-green-500 border-2' : ''}
                      `}
                      style={{ 
                        backfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                      }}
                    >
                      <CardContent className="h-full flex flex-col items-center justify-center p-3 text-center">
                        <p className={`font-heading font-bold mb-1 ${
                          difficulty === 'facile' ? 'text-lg' : 'text-base'
                        }`}>
                          {card.text}
                        </p>
                        {card.type === 'baoule' && card.phonetic && (
                          <p className="text-xs text-muted-foreground font-mono mb-2">
                            /{card.phonetic}/
                          </p>
                        )}
                        {card.type === 'baoule' && isMatched && (
                          <AudioButton 
                            text={card.text}
                            phonetic={card.phonetic}
                            size="sm"
                          />
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Victory modal */}
        {gameWon && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full animate-scale-in">
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="font-heading text-3xl font-bold mb-2">
                  Bravo ! 🎉
                </h2>
                <p className="text-muted-foreground mb-6">
                  Tu as trouvé toutes les paires !
                </p>

                {/* Stars */}
                <div className="flex justify-center gap-2 mb-6">
                  {[1, 2, 3].map((star) => (
                    <Star
                      key={star}
                      className={`w-10 h-10 ${
                        star <= getStars()
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-gray-300 dark:text-gray-600'
                      }`}
                    />
                  ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-muted">
                    <Clock className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-2xl font-bold font-mono">{formatTime(time)}</p>
                    <p className="text-xs text-muted-foreground">Temps</p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted">
                    <Target className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <p className="text-2xl font-bold">{moves}</p>
                    <p className="text-xs text-muted-foreground">Coups</p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDifficulty(null)}
                    className="flex-1"
                  >
                    Niveaux
                  </Button>
                  <Button
                    onClick={restartGame}
                    className="flex-1 gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Rejouer
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
